import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IPagamento,
  IPagamentoFiltro,
  IPagerSettings,
  ICheckoutResponse,
  ICancelarAssinaturaResponse,
  IReativarAssinaturaResponse,
  IReembolsoResponse,
  IRelatorioFaturamentoFiltro,
  IRelatorioFaturamentoResponse,
  IVincularPlanoAdmin,
  IVincularPlanoAdminResponse,
  ISincronizarPlanoStripeResponse,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { IPagamentosResponse } from './models';

@Injectable({ providedIn: 'root' })
export class PagamentosDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/pagamento`;

  // Estado com Signals
  private _pagamentos = signal<IPagamento[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<IPagamentoFiltro>({});
  private _relatorio = signal<IRelatorioFaturamentoResponse | null>(null);

  // Signals públicos (readonly)
  $pagamentos = this._pagamentos.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();
  $relatorio = this._relatorio.asReadonly();

  // Computed signals
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 20;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    const inicio = total > 0 ? offSet + 1 : 0;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  // Métodos de estado
  setPagamentos(response: IPagamentosResponse) {
    if (response.success) {
      this._pagamentos.set(response.data);
      this._paginacao.set(response.pagerSettings);
    } else {
      this.setErro(response.errorMessage || 'Erro desconhecido');
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<IPagamentoFiltro>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  // ========================================
  // OPERAÇÕES DE LISTAGEM
  // ========================================

  async carregarPagamentos(opcoes?: {
    offSet?: number;
    limit?: number;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const filtroDto = {
        ...this.$filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? '',
        ordenacao: pag.ordenacao ?? 0,
      };

      const response = await firstValueFrom(
        this.http.post<IPagamentosResponse>(
          `${this.baseUrl}/findAll`,
          filtroDto
        )
      );

      this.setPagamentos(response);
    } catch (error) {
      this.setErro('Erro ao carregar pagamentos');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async buscarPorId(id: string): Promise<IPagamento | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pagamento = await firstValueFrom(
        this.http.get<IPagamento>(`${this.baseUrl}/${id}`)
      );
      return pagamento;
    } catch (error) {
      this.setErro('Erro ao buscar pagamento');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async buscarPorPessoa(pessoaId: string): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const response = await firstValueFrom(
        this.http.get<IPagamentosResponse>(
          `${this.baseUrl}/pessoa/${pessoaId}`,
          {
            params: {
              offSet: pag.offSet?.toString() ?? '0',
              limit: pag.limit?.toString() ?? '20',
            },
          }
        )
      );

      this.setPagamentos(response);
    } catch (error) {
      this.setErro('Erro ao buscar pagamentos da pessoa');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // OPERAÇÕES DE CHECKOUT
  // ========================================

  async criarCheckout(params: {
    pessoaId: string;
    planoId: string;
    billingInterval?: 'month' | 'year';
  }): Promise<ICheckoutResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ICheckoutResponse>(`${this.baseUrl}/checkout`, params)
      );
      return response;
    } catch (error) {
      this.setErro('Erro ao criar checkout');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // OPERAÇÕES DE ASSINATURA
  // ========================================

  async cancelarAssinatura(params: {
    pessoaId: string;
    sistemaId: string;
    imediato?: boolean;
  }): Promise<ICancelarAssinaturaResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ICancelarAssinaturaResponse>(
          `${this.baseUrl}/cancelar`,
          params
        )
      );
      await this.carregarPagamentos();
      return response;
    } catch (error) {
      this.setErro('Erro ao cancelar assinatura');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async reativarAssinatura(params: {
    pessoaId: string;
    sistemaId: string;
  }): Promise<IReativarAssinaturaResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<IReativarAssinaturaResponse>(
          `${this.baseUrl}/reativar`,
          params
        )
      );
      await this.carregarPagamentos();
      return response;
    } catch (error) {
      this.setErro('Erro ao reativar assinatura');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // OPERAÇÕES DE REEMBOLSO
  // ========================================

  async criarReembolso(params: {
    pagamentoId: string;
    amount?: number;
    reason?: string;
  }): Promise<IReembolsoResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<IReembolsoResponse>(`${this.baseUrl}/reembolso`, params)
      );
      await this.carregarPagamentos();
      return response;
    } catch (error) {
      this.setErro('Erro ao criar reembolso');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // OPERAÇÕES ADMIN
  // ========================================

  async gerarRelatorioFaturamento(
    filtro: IRelatorioFaturamentoFiltro
  ): Promise<IRelatorioFaturamentoResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<IRelatorioFaturamentoResponse>(
          `${this.baseUrl}/relatorio/faturamento`,
          filtro
        )
      );
      this._relatorio.set(response);
      return response;
    } catch (error) {
      this.setErro('Erro ao gerar relatório');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async vincularPlanoAdmin(
    params: IVincularPlanoAdmin
  ): Promise<IVincularPlanoAdminResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<IVincularPlanoAdminResponse>(
          `${this.baseUrl}/admin/vincular`,
          params
        )
      );
      return response;
    } catch (error) {
      this.setErro('Erro ao vincular plano');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async sincronizarPlanoStripe(
    planoId: string
  ): Promise<ISincronizarPlanoStripeResponse | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ISincronizarPlanoStripeResponse>(
          `${this.baseUrl}/admin/sincronizar-stripe`,
          { planoId }
        )
      );
      return response;
    } catch (error) {
      this.setErro('Erro ao sincronizar com Stripe');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // NAVEGAÇÃO DE PAGINAÇÃO
  // ========================================

  async proximaPagina(): Promise<void> {
    if (this.$temProxPagina()) {
      const pag = this.$paginacao();
      const novoPagOffset = (pag.offSet ?? 0) + (pag.limit ?? 20);
      await this.carregarPagamentos({ offSet: novoPagOffset });
    }
  }

  async paginaAnterior(): Promise<void> {
    if (this.$temPagAnterior()) {
      const pag = this.$paginacao();
      const novoPagOffset = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 20));
      await this.carregarPagamentos({ offSet: novoPagOffset });
    }
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 20;
    const novoOffset = (pagina - 1) * limit;
    await this.carregarPagamentos({ offSet: novoOffset });
  }

  async buscar(filtro: IPagamentoFiltro): Promise<void> {
    this.setFiltros(filtro);
    await this.carregarPagamentos({ offSet: 0 });
  }

  async limparFiltros(): Promise<void> {
    this._filtros.set({});
    await this.carregarPagamentos({ offSet: 0 });
  }
}
